import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { PASSWORD_MIN_LENGTH } from '@constants';
import { ValidateOutput, validatePassword } from '@utils/validation';
import { InputStates, InputCheckRules, InputTypes } from '../Input';
import { RulesList } from '../RulesList';
import styles from '../Input.module.css';
import { useThemes } from '@hooks/useThemes';

export interface LabeledInputProps {
    id?: string;
    classes?: string;
    type: InputTypes;
    label: string;
    hint?: string;
    hintAlign?: 'left' | 'center' | 'right';
    min?: number;
    max?: number;
    step?: number;
    placeholder?: string;
    value: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (value: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onInput?: (value: any) => void;
    maxLength?: number;
    state?: InputStates;
    errorMessages?: string[];
    checkRules?: InputCheckRules;
    onValidationChange?: (isValid: boolean) => void;
    disabled?: boolean;
    readOnly?: boolean;
    multiple?: boolean;
}

export const LabeledInput: FC<LabeledInputProps> = ({
    id,
    classes,
    type,
    label,
    hint,
    hintAlign,
    min,
    max,
    step,
    placeholder,
    value,
    onChange,
    onInput,
    maxLength,
    state,
    errorMessages,
    checkRules,
    onValidationChange,
    disabled,
    readOnly,
    multiple,
}) => {
    const { t } = useTranslation();
    const { useGrayscaleTheme } = useThemes();

    const translations = {
        passwordRulesTitle: t('input-component.password.rules-title'),
        passwordRuleUppercaseLetter: t('input-component.rule.an-uppercase-letter'),
        passwordRuleLowercaseLetter: t('input-component.rule.a-lowercase-letter'),
        passwordRuleNumber: t('input-component.rule.a-number'),
        passwordRuleSpecialChar: t('input-component.rule.a-special-character'),
        passwordRuleMinChars: t('input-component.rule.minimum-length-characters', { n: PASSWORD_MIN_LENGTH }),
    };

    /* VALIDATION */

    // even if the input is not valid, the error should not be displayed before the user interacts with the input
    const [touched, setTouched] = useState<boolean>(false);

    // to check if field is valid, and if not, what are the validations that failed
    const [validatedInfo, setValidatedInfo] = useState<ValidateOutput | null>(
        checkRules === InputCheckRules.passwordBuild ? validatePassword(value) : null
    );

    // send validation info to callback
    useEffect(() => {
        onValidationChange && onValidationChange(validatedInfo ? validatedInfo.isValid : true);
    }, [validatedInfo, onValidationChange]);

    // check if field is valid
    const validate: (value: string) => void = useCallback(
        (value) => {
            // validate password construction
            if (checkRules === InputCheckRules.passwordBuild) {
                setValidatedInfo(validatePassword(value));
            }
        },
        [checkRules]
    );

    const inputState =
        // if there isn't any rules to check, the inputState will be the one passed as param
        !checkRules
            ? state
            : // else, the inputState also take into consideration if any validation rule is invalid
              validatedInfo?.isValid === false && touched
              ? 'error'
              : state;

    /* PASSWORD TOGGLE */

    // to toggle between hidden text in type="password" field
    const [showValue, setShowValue] = useState<boolean>(false);

    const toggleShowValue = () => {
        setShowValue(!showValue);
    };

    const commonClasses =
        styles['labeled-input'] +
        `  ${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
        ` ${inputState === 'error' ? styles['has-error'] : inputState === 'success' ? styles.success : ''}` +
        // type date specific class
        ` ${type === 'date' ? styles['date-input'] : ''}` +
        // type password specific class
        ` ${type === 'password' ? styles['toggle-password'] : ''}` +
        // extra classes
        ` ${classes || ''}`;

    return (
        <div className={commonClasses}>
            <label>{label}</label>
            <div>
                {type === InputTypes.textarea ? (
                    <textarea
                        id={id || undefined}
                        className={`form-control ${styles['form-control']}`}
                        placeholder={placeholder}
                        onChange={(e) => {
                            onChange(e.target.value);
                            validate(e.target.value);
                        }}
                        onBlur={() => setTouched(true)}
                        disabled={disabled}
                        readOnly={readOnly}
                        value={value}
                        data-testid="input"
                    />
                ) : (
                    <input
                        id={id || undefined}
                        type={type === InputTypes.password ? (!showValue ? 'password' : 'text') : type}
                        className={`form-control ${styles['form-control']}`}
                        min={min}
                        max={max}
                        step={type === InputTypes.number ? step || 'any' : undefined}
                        placeholder={placeholder}
                        value={type === InputTypes.file ? undefined : value}
                        onChange={(e) => {
                            if (type === InputTypes.file) {
                                onChange(e.target.files);
                            } else {
                                onChange(e.target.value);
                                validate(e.target.value);
                            }
                        }}
                        onInput={onInput}
                        maxLength={maxLength}
                        onBlur={() => setTouched(true)}
                        disabled={disabled}
                        readOnly={readOnly}
                        multiple={multiple}
                        data-testid="input"
                    />
                )}
                {type === 'date' && <i className="fa-light fa-calendar-days" />}
                {type === 'password' && (
                    <i className={`fa-light ${showValue ? 'fa-eye-slash' : 'fa-eye'}`} onClick={() => toggleShowValue()} />
                )}

                {type !== 'date' && type !== 'password' && (
                    <>
                        {state === 'error' && <i className="fa-light fa-triangle-exclamation" />}
                        {state === 'success' && <i className="fa-light fa-circle-check" />}
                    </>
                )}
            </div>
            {hint && (
                <span
                    className={
                        styles.hint +
                        ` ${hintAlign === 'right' ? styles['hint-right'] : hintAlign === 'center' ? styles['hint-center'] : ''}`
                    }>
                    {hint}
                </span>
            )}
            {errorMessages && (
                <div className="mt-1">
                    <ErrorMessages messages={errorMessages} />
                </div>
            )}
            {checkRules === InputCheckRules.passwordBuild && inputState === InputStates.error && (
                <RulesList
                    title={translations.passwordRulesTitle}
                    rules={[
                        { label: translations.passwordRuleUppercaseLetter, verify: validatedInfo?.rules.hasUppercaseLetter },
                        { label: translations.passwordRuleLowercaseLetter, verify: validatedInfo?.rules.hasLowercaseLetter },
                        { label: translations.passwordRuleNumber, verify: validatedInfo?.rules.hasNumber },
                        { label: translations.passwordRuleSpecialChar, verify: validatedInfo?.rules.hasSpecialChar },
                        { label: translations.passwordRuleMinChars, verify: validatedInfo?.rules.hasMinimumChars },
                    ]}
                />
            )}
        </div>
    );
};
